import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import LeadCaptureForm from "@/components/LeadCaptureForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LEAD_TOKEN_RE = /<\s*lead[-_]capture\s*\/?\s*>/i;

const SUGGESTED = [
  "How much does it cost?",
  "Does this work for restaurants?",
  "How long does it take?",
  "Can I talk to Lovefavor?",
];

const GREETING = {
  role: "assistant",
  content:
    "Hey — I'm the Always On Sales AI agent, the same kind we install for clients. Ask me anything about the event, the pricing, or how this would work for your business.",
};

// Returns { display, leadCapture: bool }
function parseAssistant(text) {
  const has = LEAD_TOKEN_RE.test(text);
  return {
    display: has ? text.replace(LEAD_TOKEN_RE, "").trim() : text,
    leadCapture: has,
  };
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [lastUserQuestion, setLastUserQuestion] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const sendMessage = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setLastUserQuestion(text);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);

    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!resp.ok) {
        throw new Error(`Server error ${resp.status}`);
      }
      const data = await resp.json();
      if (!sessionId && data.session_id) setSessionId(data.session_id);
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: data.reply || "" };
        return next;
      });
    } catch (_err) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content:
            "I'm having trouble reaching my brain right now. Try again in a moment, or drop your email in the form on this page and Lovefavor will follow up personally.",
        };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <button
        data-testid={TEST_IDS.chatFab}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Open chat"}
        className={`fixed bottom-28 right-6 z-50 h-16 w-16 rounded-full flex items-center justify-center text-white transition-transform hover:scale-105 ${open ? "" : "chat-fab-pulse"}`}
        style={{
          background:
            "linear-gradient(135deg, #0055ff 0%, #00f0ff 100%)",
        }}
      >
        {open ? <X size={26} /> : <MessageSquare size={26} />}
      </button>

      {open && (
        <div
          data-testid={TEST_IDS.chatPanel}
          className="fixed bottom-48 right-6 z-50 w-[92vw] max-w-[420px] h-[600px] max-h-[75vh] flex flex-col rounded-2xl overflow-hidden"
          style={{
            background: "rgba(11, 17, 32, 0.92)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(0,85,255,0.25)",
          }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div
                className="h-10 w-10 rounded-full flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, #0055ff 0%, #00f0ff 100%)",
                }}
              >
                <Sparkles size={18} className="text-white" />
              </div>
              <div>
                <div className="font-display font-bold text-white text-base leading-tight">
                  Always On Sales
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-cyan-300/80">
                  Live AI Agent · Online
                </div>
              </div>
            </div>
            <button
              data-testid={TEST_IDS.chatClose}
              onClick={() => setOpen(false)}
              className="text-white/60 hover:text-white"
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>

          <div
            ref={scrollRef}
            data-testid={TEST_IDS.chatMessages}
            className="flex-1 overflow-y-auto chat-scroll px-5 py-5 space-y-4"
          >
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              const { display, leadCapture } = isUser
                ? { display: m.content, leadCapture: false }
                : parseAssistant(m.content);
              const isLast = i === messages.length - 1;
              return (
                <div key={i}>
                  <div
                    className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        isUser
                          ? "bg-[#D4AF37] text-[#030712] font-medium"
                          : "bg-white/8 border border-white/10 text-white/90"
                      }`}
                      style={
                        isUser
                          ? { borderBottomRightRadius: 4 }
                          : { borderBottomLeftRadius: 4 }
                      }
                    >
                      {display || (
                        <span className="inline-flex gap-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse" />
                          <span
                            className="h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse"
                            style={{ animationDelay: "0.15s" }}
                          />
                          <span
                            className="h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse"
                            style={{ animationDelay: "0.3s" }}
                          />
                        </span>
                      )}
                    </div>
                  </div>
                  {!isUser && leadCapture && isLast && !sending && (
                    <LeadCaptureForm
                      sessionId={sessionId}
                      question={lastUserQuestion}
                    />
                  )}
                </div>
              );
            })}

            {messages.length <= 1 && (
              <div className="pt-2 flex flex-wrap gap-2">
                {SUGGESTED.map((s) => (
                  <button
                    key={s}
                    onClick={() => sendMessage(s)}
                    className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:bg-white/10 hover:text-white transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-white/10 p-3 flex items-end gap-2">
            <textarea
              ref={inputRef}
              data-testid={TEST_IDS.chatInput}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask anything about the event…"
              className="flex-1 resize-none rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/60 focus:bg-white/10"
              style={{ maxHeight: 120 }}
            />
            <button
              data-testid={TEST_IDS.chatSend}
              onClick={() => sendMessage()}
              disabled={sending || !input.trim()}
              className="h-10 w-10 rounded-xl flex items-center justify-center text-[#030712] disabled:opacity-40"
              style={{
                background:
                  "linear-gradient(135deg, #D4AF37 0%, #F3CE5E 100%)",
              }}
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
